import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PromptLibrary } from "@/components/PromptLibrary";
import { ChatSessions } from "@/components/ChatSessions";
import { Sparkles, SendHorizonal, RotateCcw, Paperclip, X, Download, FileSpreadsheet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODELS = [
  { id: "claude-opus-4-8", label: "Opus 4.8 · most capable" },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6 · balanced" },
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5 · fastest" },
];

const SUGGESTIONS = [
  "Which channel made the most revenue in the last 30 days?",
  "What are my total seller fees and which fee type is highest?",
  "Which products need reordering right now?",
  "Compare my Amazon vs Flipkart settlement payouts",
  "Where am I overpriced compared to my own website?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [model, setModel] = useState(() => localStorage.getItem("chat_model") || "claude-opus-4-8");
  const [sessionId, setSessionId] = useState(() => {
    let id = localStorage.getItem("chat_session");
    if (!id) { id = crypto.randomUUID(); localStorage.setItem("chat_session", id); }
    return id;
  });
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - attachments.length);
    files.forEach((f) => {
      if (f.size > 8 * 1024 * 1024) return toast.error(`${f.name} is over 8MB`);
      const reader = new FileReader();
      reader.onload = () => {
        const data_base64 = reader.result.split(",")[1];
        setAttachments((a) => [...a, { name: f.name, mime: f.type || "application/octet-stream", data_base64 }]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  useEffect(() => {
    api.get(`/chat/history?session_id=${sessionId}`).then(({ data }) => setMessages(data.messages || []));
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    const atts = attachments;
    setAttachments([]);
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message, attachments: atts.map((a) => a.name) }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("access_token") ? { Authorization: `Bearer ${localStorage.getItem("access_token")}` } : {}) },
        body: JSON.stringify({ message, session_id: sessionId, attachments: atts, model }),
      });
      if (!res.ok) {
        const body = (await res.text()).slice(0, 300);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: `⚠ The AI service returned an error (HTTP ${res.status}). ${body}` };
          return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.status) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], status: obj.status };
                return copy;
              });
              continue;
            }
            const chunk = obj.token || (obj.error ? `\n\n⚠ ${obj.error}` : "");
            if (chunk) setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          } catch (e) { /* skip partial */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the AI service. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      setRefreshKey((k) => k + 1);
    }
  };

  const downloadMd = (content) => {
    const blob = new Blob([content], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "claude-report.md";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadXlsx = async (content) => {
    try {
      const res = await api.post("/export/xlsx", { markdown: content, title: "claude-report" }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "claude-report.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const newChat = () => {
    const id = crypto.randomUUID();
    localStorage.setItem("chat_session", id);
    setSessionId(id);
    setMessages([]);
  };

  const selectSession = (id) => {
    localStorage.setItem("chat_session", id);
    setSessionId(id);
  };

  return (
    <div className="max-w-6xl mx-auto flex gap-4 h-[calc(100vh-9rem)]" data-testid="assistant-page">
      <ChatSessions sessionId={sessionId} onSelect={selectSession} onNew={newChat} refreshKey={refreshKey} />
      <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-heading text-base font-semibold tracking-tight">Claude Analytics Assistant</h2>
            <p className="text-xs text-muted-foreground">Ask anything about your sales, fees, settlements, inventory or prices.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={model} onValueChange={(v) => { setModel(v); localStorage.setItem("chat_model", v); }}>
            <SelectTrigger className="w-[190px] h-9 rounded-full text-xs" data-testid="model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <PromptLibrary onUse={(p) => setInput(p)} />
          <Button variant="outline" size="sm" onClick={newChat} data-testid="new-chat-button" className="rounded-full">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> New chat
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-muted/40 border border-border rounded-lg p-5 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <p className="text-sm text-muted-foreground">Try one of these:</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="suggestion-chip"
                  className="text-xs px-3.5 py-2 rounded-full border border-border bg-card hover:border-blue-600 hover:text-blue-600 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-4 py-3 text-sm ${
              m.role === "user" ? "bg-blue-600 text-white" : "bg-card border border-border"
            }`} data-testid={`chat-message-${m.role}`}>
              {m.role === "assistant" ? (
                <div>
                  <div className="md-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || (streaming && i === messages.length - 1 ? `*${m.status || "…"}*` : "")}</ReactMarkdown>
                  </div>
                  {m.content && !(streaming && i === messages.length - 1) && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-border">
                      <button onClick={() => downloadMd(m.content)} data-testid="download-md-button"
                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-blue-600 transition-colors">
                        <Download className="h-3 w-3" aria-hidden="true" /> .md
                      </button>
                      <button onClick={() => downloadXlsx(m.content)} data-testid="download-xlsx-button"
                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-green-600 transition-colors">
                        <FileSpreadsheet className="h-3 w-3" aria-hidden="true" /> .xlsx
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {m.content}
                  {m.attachments?.length > 0 && (
                    <p className="text-[10px] mt-1 opacity-80">📎 {m.attachments.join(", ")}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="attachment-chips">
          {attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-card border border-border rounded-full px-3 py-1.5">
              <Paperclip className="h-3 w-3" aria-hidden="true" /> {a.name}
              <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} aria-label="Remove attachment"
                className="text-muted-foreground hover:text-red-600">
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input ref={fileRef} type="file" multiple accept="image/*,.xlsx,.xlsm,.csv,.txt" className="hidden" onChange={onFiles} data-testid="chat-file-input" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="chat-attach-button"
          className="rounded-full h-11 w-11 p-0 shrink-0" title="Attach photos or Excel/CSV files">
          <Paperclip className="h-4 w-4" aria-hidden="true" />
        </Button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. Why did fees jump on Amazon this month?"
          data-testid="chat-input"
          className="flex-1 h-11 rounded-full border border-border bg-card px-5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
        />
        <Button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send-button" className="rounded-full h-11 w-11 p-0">
          <SendHorizonal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>
      </div>
    </div>
  );
}
